import { Box, Container, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { format } from 'date-fns';
import { Formik, Form } from 'formik';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { FormikSelectAsyncPaginate } from 'components/Formik';
import FormikDateRangePicker from 'components/Formik/FormikDateRangePicker';
import Title from 'components/Title';
import { roles } from 'global/rolesAndPermissions';
import { fetchPaginatedEmployee } from 'hooks/employee/useFetchPaginatedEmployee';
import { fetchPaginatedEmployeeGroup } from 'hooks/employee/useFetchPaginatedEmployeeGroups';
import { useFetchReportsByScheduleVsHoursWorked } from 'hooks/reports/useFetchReportsByScheduleVsHoursWorked';
// import { addMonthsToDate } from 'utils/helpers';
import { getAsyncSelectLoadOptions, getUserStore } from 'utils/helpers';

import ScheduleVsHoursReportTable from './ScheduleVsHoursReportTable';
import { initialScheduledVsHoursWorkedFormData, scheduledVsHoursWorkedValidationSchema } from './schema';

const ScheduleVsHoursWorkedReport: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'reports']);

  const formikRef = useRef<any>();
  const userStore = getUserStore();

  const { data, status, mutateAsync } = useFetchReportsByScheduleVsHoursWorked();

  const employeeLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedEmployee, {
      page,
      search,
      active: '1',
      billing_rate_type: '0',
      report: true,
    });
  };

  const groupLoadOptions = (page: number, search: string, jobId: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedEmployeeGroup, { page, search, jobId: jobId });
  };

  const handleFormSubmit = (values: typeof initialScheduledVsHoursWorkedFormData) => {
    const postData = {
      user_id:
        userStore?.userInfo?.roleId === roles.EMPLOYEE
          ? userStore?.userInfo?.userId
          : values.user_id?.userId || undefined,
      group_id: values.group_id?.id || undefined,
      start_date: values.date_range[0] !== null ? format(values.date_range[0], 'MM-dd-yyyy') : '',
      end_date: values.date_range[1] !== null ? format(values.date_range[1], 'MM-dd-yyyy') : '',
    };

    mutateAsync(postData);
  };

  return (
    <div>
      <Title>{t('reports:scheduledVsHoursWorked')}</Title>
      <Flex mt="5" justifyContent="center">
        {userStore?.userInfo?.roleId === roles.EMPLOYEE ? (
          <Flex mt="5" justifyContent="center">
            <Formik
              initialValues={initialScheduledVsHoursWorkedFormData}
              validationSchema={scheduledVsHoursWorkedValidationSchema}
              onSubmit={handleFormSubmit}
            >
              {formikBag => {
                formikRef.current = formikBag;
                return (
                  <Form autoComplete="off">
                    <Grid
                      display="inline-grid"
                      templateColumns={{ base: '1fr', sm: '280px 1fr' }}
                      gap={{ base: 0, sm: 4 }}
                    >
                      <GridItem>
                        <FormikDateRangePicker
                          label={t('common:dateRange')}
                          name="date_range"
                          formControlProps={{ isRequired: true }}
                        />
                      </GridItem>
                      <GridItem mt={{ base: 0, sm: '6' }}>
                        <Button type="submit" colorScheme="secondary" isLoading={status === 'loading'}>
                          {t('reports:generateReport')}
                        </Button>
                      </GridItem>
                    </Grid>
                  </Form>
                );
              }}
            </Formik>
          </Flex>
        ) : (
          <Container mt="5" maxW="container.lg" p="0">
            <Formik
              initialValues={initialScheduledVsHoursWorkedFormData}
              validationSchema={scheduledVsHoursWorkedValidationSchema}
              onSubmit={handleFormSubmit}
            >
              {formikBag => {
                formikRef.current = formikBag;
                const { setFieldValue } = formikBag;

                return (
                  <Form autoComplete="off">
                    <Flex
                      flexWrap="wrap"
                      sx={{ '.valueContainerWrapper': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
                      mb="1"
                    >
                      <Flex
                        mr="2"
                        width={{ base: '100%', lg: '65%' }}
                        flexDirection={{ base: 'column', md: 'row' }}
                        alignItems="flex-start"
                      >
                        <FormikSelectAsyncPaginate
                          name="user_id"
                          label={t('common:user')}
                          loadOptions={employeeLoadOptions}
                          onChangeCallback={() => {
                            setFieldValue('group_id', '');
                          }}
                          selectProps={{
                            isMulti: false,
                            placeholder: t('common:selectUser'),
                            getOptionLabel: (option: any) => option?.firstName + ' ' + option?.lastName,
                            getOptionValue: (option: any) => option.userId,
                          }}
                        />
                        <Text fontSize="sm" mx={{ base: 'auto', md: '2' }} mt={{ md: '8' }} textTransform="uppercase">
                          {t('common:or')}
                        </Text>
                        <FormikSelectAsyncPaginate
                          name="group_id"
                          label={t('common:group')}
                          loadOptions={groupLoadOptions}
                          onChangeCallback={() => {
                            setFieldValue('user_id', '');
                          }}
                          selectProps={{
                            isMulti: false,
                            placeholder: t('reports:selectAGroup'),
                            getOptionLabel: (option: any) => option?.name,
                            getOptionValue: (option: any) => option.id,
                          }}
                        />
                      </Flex>
                      <Box width={{ base: '100%', md: '50%', lg: 'calc(35% - 8px)' }}>
                        <FormikDateRangePicker
                          label={t('common:dateRange')}
                          name="date_range"
                          formControlProps={{ isRequired: true }}
                        />
                      </Box>
                    </Flex>
                    <Box textAlign="right">
                      <Button
                        type="submit"
                        colorScheme="secondary"
                        isLoading={status === 'loading'}
                        alignSelf="flex-start"
                      >
                        {t('reports:generateReport')}
                      </Button>
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          </Container>
        )}
      </Flex>
      {data ? <ScheduleVsHoursReportTable data={data} dateRange={formikRef?.current?.values?.date_range} /> : null}
    </div>
  );
};

export default ScheduleVsHoursWorkedReport;
